/* eslint-disable no-case-declarations */
/* eslint-disable no-param-reassign */
/* eslint-disable no-unused-expressions */
import { createSelector } from 'reselect';
import { compareValues } from '@xto10x/core/lib/app.utils';
import { AttributeType, createAttribute } from 'utils/inviteUtils';
import { convertListToMap } from 'utils/app.utils';
import {
  createRootHierarchyFromUsers,
  createUpdatedHierarchyWithDR
} from '../inviteUtils';
import { actions } from './actions';
import { createHrbpUserList } from '../../Settings/components/Permissions/Permissions.utils';

const initialState = {
  allUsersMap: null,
  allUsersMapForPeopleSelector: null,
  allUsers: [],
  activeUsers: [],
  pendingUsers: [],
  isAllUsersV2: false,
  fileDataOfUsers: {},
  founders: [],
  searchHierarchy: {},
  orgHierarchy: {},
  orgHierarchyUserMap: {},
  l1Leaders: [],
  allFunctions: [],
  danglingUsers: [],
  cyclicUsers: [],
  demographicsMasterData: {},
  orgDemographicKeys: {},
  isUsersLoading: true,
  allHrbps: {},
  allHrbpList: [],
  onGoingUserInviteStatus: null
};

export const userListWithManagerDetails = (targetUserList, allUsersList) => {
  // Create a map of all users for reference
  const allUsersMap = new Map();
  allUsersList.forEach(user => {
    allUsersMap.set(user.user_reference_id, user);
  });

  // Only enhance the target list users with their manager details
  return targetUserList.map(user => {
    // Create a copy to avoid mutating the original user object
    const enhancedUser = { ...user };

    // If this user has a manager, enhance the manager info
    if (enhancedUser.manager && enhancedUser.manager.user_reference_id) {
      const managerUser = allUsersMap.get(
        enhancedUser.manager.user_reference_id
      );
      if (managerUser) {
        enhancedUser.manager = {
          ...enhancedUser.manager,
          name: managerUser.name,
          email: managerUser.email
        };
      }
    }

    return enhancedUser;
  });
};

const appendAdditionalAttributes = (
  list,
  allUsersListWithoutManagerDetails,
  isV3Api,
  advancedOKRUsers
) => {
  let users = [];
  if (isV3Api) {
    users = userListWithManagerDetails(list, allUsersListWithoutManagerDetails);
  } else {
    users = list;
  }
  return (
    users?.map(user => ({
      ...user,
      is_basic_okr_user: isV3Api
        ? !advancedOKRUsers.includes(user.user_reference_id)
        : !!(
            user.user_product_details_list
              ?.find(
                ({ product_reference_id }) => product_reference_id === 'okr'
              )
              ?.accessible_modules?.indexOf('ADVANCED_OKR') === -1
          ),
      status: user.status || user.overall_status
    })) || []
  );
};

const convertUserListToMap = usersList => {
  const userMap = {};
  usersList.forEach(user => {
    userMap[user.user_reference_id] = user;
  });
  return userMap;
};

const convertUserListToMapForPeopleSelector = usersList => {
  const userMap = {};
  usersList.forEach(user => {
    userMap[user.user_reference_id] = {
      ...user,
      id: user.user_reference_id,
      role: user.role,
      ...user.user_attributes,
      manager_name: user.manager?.name || user.manager?.email
    };
  });
  return userMap;
};

export const getAllUsersMapSelector = createSelector(
  state => state.inviteReducer.allUsersMap,
  allUsersMap => allUsersMap
);

export const getAllUsersMapForPeopleSelectorSelector = createSelector(
  state => state.inviteReducer.allUsersMapForPeopleSelector,
  allUsersMap => allUsersMap
);

// Memorized  Selector Function
export const getActiveUsersSelector = createSelector(
  state => state.inviteReducer.activeUsers,
  activeUsers => activeUsers
);

// Memorized  Selector Function
export const getPendingUsersSelector = createSelector(
  state => state.inviteReducer.pendingUsers,
  pendingUsers => pendingUsers
);

// Memorized  Selector Function
export const getFileUploadDataOfUsersSelector = createSelector(
  state => state.inviteReducer.fileDataOfUsers,
  fileDataOfUsers => fileDataOfUsers
);

export const getFounderDetailsSelector = createSelector(
  state => state.inviteReducer.founders,
  founders => founders
);

export const getOrgHierarchySelector = createSelector(
  state => state.inviteReducer.orgHierarchy,
  orgHierarchy => orgHierarchy
);

export const getOrgHierarchyUserMapSelector = createSelector(
  state => state.inviteReducer.orgHierarchyUserMap,
  orgHierarchyUserMap => orgHierarchyUserMap
);

export const getSearchHierarchySelector = createSelector(
  state => state.inviteReducer.searchHierarchy,
  searchHierarchy => searchHierarchy
);

export const getDanglingUsersSelector = createSelector(
  state => state.inviteReducer.danglingUsers,
  danglingUsers => danglingUsers
);

export const getCyclicUsersSelector = createSelector(
  state => state.inviteReducer.cyclicUsers,
  cyclicUsers => cyclicUsers
);

export const getMissingDemographicsUsersSelector = createSelector(
  state => state.inviteReducer.missingDemographicsUsers,
  missingDemographicsUsers => missingDemographicsUsers
);

export const getL1LeadersSelector = createSelector(
  state => state.inviteReducer.l1Leaders,
  l1Leaders => l1Leaders
);

export const getFunctionsSelector = createSelector(
  state => state.inviteReducer.allFunctions,
  allFunctions => allFunctions
);

export const getDemographicMasterDataSelector = createSelector(
  state => state.inviteReducer.demographicsMasterData,
  demographicsMasterData => demographicsMasterData
);

export const getOrgDemographicKeysSelector = createSelector(
  state => state.inviteReducer.orgDemographicKeys,
  orgDemographicKeys => orgDemographicKeys
);

export const getIsUserLoadingSelector = createSelector(
  state => state.inviteReducer.isUsersLoading,
  isUsersLoading => isUsersLoading
);

export const getAllUsersSelector = createSelector(
  state => state.inviteReducer.allUsers,
  allUsers => allUsers
);

export const getIsOrgSubscribedSelector = createSelector(
  state => state.inviteReducer.orgSubscription,
  orgSubscription => orgSubscription
);

export const getHrbpsSelector = createSelector(
  state => state.inviteReducer.allHrbps,
  allHrbps => allHrbps
);

export const getHrbpsListSelector = createSelector(
  state => state.inviteReducer.allHrbpList,
  allHrbpList => allHrbpList
);

export const getIsOngoingUserInviteSelector = createSelector(
  state => state.inviteReducer.onGoingUserInviteStatus,
  onGoingUserInviteStatus => onGoingUserInviteStatus
);

const inviteReducer = (state = initialState, payload = {}) => {
  switch (payload.type) {
    case actions.SAVE_USERS_LIST:
      // leaving this declaration so that
      // later on we will have option to add mandatory attributes
      const mandatoryAttributes = [];
      const isV3Api = !payload.isUserDetailsV2;
      const allUsersListWithoutManagerDetails = [
        ...payload.list.active_users,
        ...payload.list.pending_users,
        ...payload.list.revoked_users
      ];
      const advancedOKRUsers = payload.list?.advancedOKRUsers;
      const activeUsers = appendAdditionalAttributes(
        payload.list.active_users.sort(compareValues('name')),
        allUsersListWithoutManagerDetails,
        isV3Api,
        advancedOKRUsers
      );
      const pendingUsers = appendAdditionalAttributes(
        payload.list.pending_users.sort(compareValues('email')),
        allUsersListWithoutManagerDetails,
        isV3Api,
        advancedOKRUsers
      );
      const revokedUsers = appendAdditionalAttributes(
        payload.list.revoked_users.sort(compareValues('name')),
        allUsersListWithoutManagerDetails,
        isV3Api,
        advancedOKRUsers
      );
      const combinedUserList = activeUsers
        .concat(pendingUsers)
        .concat(revokedUsers);
      const missingDemographicsUsers = combinedUserList.filter(
        u =>
          !mandatoryAttributes.every(
            attribute => u?.user_attributes?.[attribute]
          )
      );
      return {
        ...state,
        activeUsers,
        pendingUsers,
        isUsersLoading: false,
        missingDemographicsUsers,
        allUsersMap: convertUserListToMap(combinedUserList),
        allUsersMapForPeopleSelector: convertUserListToMapForPeopleSelector(
          combinedUserList
        ),
        allUsers: combinedUserList,
        isAllUsersV2: payload.isUserDetailsV2 ?? false
      };

    case actions.SAVE_ORG_SUBSCRIPTION_RESPONSE: {
      return {
        ...state,
        orgSubscription: payload.data
      };
    }

    case actions.REMOVE_ALL_DATA:
      return {
        ...state,
        activeUsers: [],
        pendingUsers: [],
        missingDemographicsUsers: []
      };

    case actions.FILE_UPLOAD_USER_DATA: {
      const fileData = {
        ext: payload.ext || '',
        content: payload.path
      };
      return {
        ...state,
        fileDataOfUsers: fileData
      };
    }

    case actions.GET_FOUNDER_DETAILS_RESPONSE: {
      const allFounders = [...payload.data.founders];
      const orgHierarchy = createRootHierarchyFromUsers(allFounders);
      const newDataInOrgHierarchyUserMap = convertListToMap(
        allFounders,
        'user_reference_id'
      );
      const orgHierarchyUserMap = {
        ...state.orgHierarchyUserMap,
        ...newDataInOrgHierarchyUserMap
      };
      return {
        ...state,
        founders: allFounders,
        orgHierarchy,
        orgHierarchyUserMap
      };
    }

    case actions.GET_DIRECT_REPORTS_IN_ORG_HIERARCHY_RESPONSE: {
      const { viaSearch } = payload;
      const newHierarchy = createUpdatedHierarchyWithDR(
        { ...state[!viaSearch ? 'orgHierarchy' : 'searchHierarchy'] },
        payload
      );

      const newDataInOrgHierarchyUserMap = viaSearch
        ? {}
        : convertListToMap(payload.data, 'user_reference_id');
      const orgHierarchyUserMap = {
        ...state.orgHierarchyUserMap,
        ...newDataInOrgHierarchyUserMap
      };
      return {
        ...state,
        [!viaSearch ? 'orgHierarchy' : 'searchHierarchy']: newHierarchy,
        orgHierarchyUserMap
      };
    }

    case actions.SET_SEARCH_HIERARCHY: {
      return {
        ...state,
        searchHierarchy: createRootHierarchyFromUsers(payload.data)
      };
    }

    case actions.VALIDATE_HIERARCHY_RESPONSE: {
      const danglingUsers = Object.values(
        payload.data.user_model_map
      ).filter(user =>
        payload.data.dangling_users.includes(user.user_reference_id)
      );

      payload.data.cycles.forEach(user => {
        user[0].userDetail = payload.data.user_model_map[user[0].user_id];
        user[0].managerDetail = payload.data.user_model_map[user[0].manager_id];
      });

      return {
        ...state,
        danglingUsers,
        cyclicUsers: payload.data.cycles
      };
    }

    case actions.GET_L1LEADERS_RESPONSE: {
      const leadersMap = {};
      const leaders = payload.data.sort((a, b) =>
        // eslint-disable-next-line no-nested-ternary
        a.name < b.name ? -1 : b.name < a.name ? 1 : 0
      );
      leaders.forEach(i => {
        leadersMap[i.user_reference_id] = i;
      });
      return {
        ...state,
        l1Leaders: leadersMap
      };
    }

    case actions.GET_FUNCTIONS_WITH_OWNER_RESPONSE: {
      const functionalUnits = payload.data.function_models;
      return {
        ...state,
        allFunctions: functionalUnits
      };
    }

    case actions.CREATE_FUNCTION_RESPONSE: {
      const addedFunction = payload.data;
      const updatedFunctions = [...state.allFunctions].filter(
        i => i.function_id !== addedFunction.function_id
      );
      updatedFunctions.push(addedFunction);
      const updatedL1Leaders = { ...state.l1Leaders };
      updatedL1Leaders[addedFunction.owner.user_reference_id].functions = [
        addedFunction
      ];
      return {
        ...state,
        allFunctions: updatedFunctions,
        l1Leaders: updatedL1Leaders
      };
    }

    case actions.REMOVE_FUNCTION_RESPONSE: {
      const removedFunction = payload.data;
      const updatedFunctions = [...state.allFunctions].filter(
        i => i.function_id !== removedFunction.function_id
      );
      updatedFunctions.push(removedFunction);
      const removedOwner = [...state.allFunctions].filter(
        i => i.function_id === removedFunction.function_id
      )[0];
      const updatedL1Leaders = { ...state.l1Leaders };
      if (removedOwner)
        updatedL1Leaders[removedOwner.owner.user_reference_id].functions = [];
      return {
        ...state,
        allFunctions: updatedFunctions,
        l1Leaders: updatedL1Leaders
      };
    }

    case actions.GET_DEMOGRAPHIC_MASTER_DATA_RESPONSE: {
      const rawData = { user_attributes_master_data: {} };
      if (payload.data?.user_attributes_master_data) {
        Object.keys(payload.data.user_attributes_master_data).forEach(key => {
          rawData.user_attributes_master_data[
            key
          ] = payload.data.user_attributes_master_data[key].filter(
            dataPoint => !!dataPoint
          );
        });
      }
      return {
        ...state,
        demographicsMasterData: rawData
      };
    }

    case actions.SAVE_ORG_DEMOGRAPHIC_KEYS: {
      const whiteListedAttributes = {
        ...payload.data.user_demographics_attributes_white_list
      };
      // if the attribute is mandatory, then it should be added to the mandatory attributes list
      const mdtAttributes = {};
      Object.keys(whiteListedAttributes).forEach(key => {
        const type = mdtAttributes[key]
          ? AttributeType.MANDATORY
          : AttributeType.OPTIONAL;
        whiteListedAttributes[key] = createAttribute(
          key,
          type,
          whiteListedAttributes[key]
        );
        // Add awards check here
      });
      return {
        ...state,
        orgDemographicKeys: whiteListedAttributes
      };
    }

    case actions.UPDATE_USER_IN_LIST: {
      const tempList = [...state.allUsers];
      const tempActiveUsers = [...state.activeUsers];
      const tempPendingUsers = [...state.pendingUsers];
      let ind = tempList.findIndex(
        a => a.user_reference_id === payload.user.user_reference_id
      );
      if (ind > -1) tempList[ind] = payload.user;

      ind = tempActiveUsers.findIndex(
        a => a.user_reference_id === payload.user.user_reference_id
      );
      if (ind > -1) tempActiveUsers[ind] = payload.user;

      ind = tempPendingUsers.findIndex(
        a => a.user_reference_id === payload.user.user_reference_id
      );
      if (ind > -1) tempPendingUsers[ind] = payload.user;

      const tempUserMap = { ...state.allUsersMap };
      tempList[payload.user.user_reference_id] = payload.user;

      return {
        ...state,
        allUsers: tempList,
        allUsersMap: tempUserMap,
        activeUsers: tempActiveUsers,
        pendingUsers: tempPendingUsers
      };
    }

    case actions.GET_HRBPS_LIST_RESPONSE: {
      return {
        ...state,
        allHrbps: payload.data,
        allHrbpList: createHrbpUserList(payload.data, state.allUsersMap)
      };
    }

    case actions.SAVE_ONGOING_USER_INVITE_STATUS: {
      return {
        ...state,
        onGoingUserInviteStatus: payload.data
      };
    }

    case actions.UPDATE_HRBP_LIST_RESPONSE: {
      const { data } = payload;
      const newAllHrbps = { ...state.allHrbps };
      if (!newAllHrbps?.hrbp_users_map) {
        newAllHrbps.hrbp_users_map = {};
      }
      if (data?.hrbp_list?.length > 0) {
        Object.values(data.hrbp_list).forEach(newHrbpId => {
          newAllHrbps.hrbp_users_map[newHrbpId] = [];
        });
      }
      return {
        ...state,
        allHrbps: newAllHrbps,
        allHrbpList: createHrbpUserList(newAllHrbps, state.allUsersMap)
      };
    }

    case actions.REMOVE_HRBP_LIST_RESPONSE: {
      const { data } = payload;
      const newAllHrbps = { ...state.allHrbps };
      if (data?.hrbp_list?.length > 0) {
        Object.values(data.hrbp_list).forEach(removeHrbpId => {
          delete newAllHrbps.hrbp_users_map[removeHrbpId];
        });
      }
      return {
        ...state,
        allHrbps: newAllHrbps,
        allHrbpList: createHrbpUserList(newAllHrbps, state.allUsersMap)
      };
    }

    case actions.UPDATE_MANAGER_HRBPS_RESPONSE: {
      const { managerId, data } = payload;
      const newOrgHierarchyMap = {
        ...state.orgHierarchyUserMap
      };

      if (newOrgHierarchyMap?.[managerId]) {
        const currentManagerData = newOrgHierarchyMap[managerId];
        const currentIndirectHrbps =
          currentManagerData?.hrbp?.indirect_hrbp || [];
        const newDirectHrbps = data?.direct_hrbp || [];
        const newHrbpData = {
          direct_hrbp: newDirectHrbps,
          indirect_hrbp: currentIndirectHrbps
        };
        newOrgHierarchyMap[managerId] = {
          ...currentManagerData,
          hrbp: newHrbpData
        };
      }

      return {
        ...state,
        orgHierarchyUserMap: newOrgHierarchyMap
      };
    }

    default:
      return {
        ...state
      };
  }
};

export default inviteReducer;
